import React from 'react';
import {
  IndianRupee, Mail, MessageCircle, FileText,
  Percent, ShoppingBag, Clock, CheckCircle,
} from 'lucide-react';

export default function Earnings() {
  return (
    <div style={S.page}>

      {/* Header */}
      <div style={S.header}>
        <div style={S.title}>Billing & Earnings</div>
        <div style={S.sub}>Your payout system</div>
      </div>

      {/* Coming soon banner */}
      <div style={S.comingSoon}>
        <div style={S.csIcon}>
          <IndianRupee size={32} color='#fff' />
        </div>
        <div style={S.csTitle}>Billing System — Coming Soon!</div>
        <div style={S.csText}>
          Your complete billing and payout system is currently under development.
          These features will be available very soon.
        </div>
      </div>

      {/* How it will work */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <FileText size={16} color='#ff6b35' />
          <span>How Billing Will Work</span>
        </div>

        {[
          {
            icon: <ShoppingBag size={18} color='#3b82f6' />,
            bg: '#eff6ff',
            title: 'Original Item Price',
            desc: 'Earnings will be calculated based on the original price set in your menu — not the customer-facing inflated price.',
          },
          {
            icon: <Percent size={18} color='#ef4444' />,
            bg: '#fef2f2',
            title: '18% Platform Commission',
            desc: 'An 18% platform commission will be deducted from the original order amount for every delivered order.',
          },
          {
            icon: <IndianRupee size={18} color='#10b981' />,
            bg: '#f0fdf4',
            title: 'Net Payable Amount',
            desc: 'Original Amount − 18% Commission = Your final payout amount.',
          },
          {
            icon: <Clock size={18} color='#8b5cf6' />,
            bg: '#f5f3ff',
            title: 'Daily Settlement',
            desc: 'A daily summary invoice will be automatically generated every night covering all delivered orders of the day.',
          },
          {
            icon: <Mail size={18} color='#f97316' />,
            bg: '#fff7ed',
            title: 'Email Invoice',
            desc: 'Your daily invoice will be automatically sent to your registered email address in PDF format.',
          },
          {
            icon: <MessageCircle size={18} color='#06b6d4' />,
            bg: '#ecfeff',
            title: 'WhatsApp Invoice',
            desc: 'The same invoice will also be delivered to your registered WhatsApp number with an instant notification.',
          },
        ].map((item, i) => (
          <div key={i} style={S.row}>
            <div style={{ ...S.rowIcon, background: item.bg }}>
              {item.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={S.rowTitle}>{item.title}</div>
              <div style={S.rowDesc}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Example calculation */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <IndianRupee size={16} color='#ff6b35' />
          <span>Example Calculation</span>
        </div>

        <div style={S.exampleBox}>
          <div style={S.exRow}>
            <span style={S.exLabel}>Original Order Value</span>
            <span style={S.exVal}>₹500</span>
          </div>
          <div style={S.exDivider} />
          <div style={S.exRow}>
            <span style={{ ...S.exLabel, color: '#ef4444' }}>Platform Commission (18%)</span>
            <span style={{ ...S.exVal, color: '#ef4444' }}>− ₹90</span>
          </div>
          <div style={{ ...S.exDivider, background: '#10b981', opacity: 0.3 }} />
          <div style={S.exRow}>
            <span style={{ ...S.exLabel, color: '#10b981', fontWeight: 800 }}>Your Payout</span>
            <span style={{ ...S.exVal, color: '#10b981', fontSize: 20 }}>₹410</span>
          </div>
        </div>
      </div>

      {/* Status note */}
      <div style={S.statusCard}>
        <CheckCircle size={16} color='#10b981' style={{ flexShrink: 0, marginTop: 2 }} />
        <span style={{ fontSize: 13, color: '#555', flex: 1, lineHeight: 1.5 }}>
          Once the billing system is launched, you will receive a notification and this page will be updated automatically.
        </span>
      </div>

    </div>
  );
}

const S = {
  page:       { padding: '16px', paddingBottom: 80, background: '#f8f8f8', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif" },
  header:     { marginBottom: 16 },
  title:      { fontSize: 22, fontWeight: 800, color: '#1a1a1a' },
  sub:        { fontSize: 13, color: '#888', marginTop: 3 },

  comingSoon: { background: 'linear-gradient(135deg,#ff6b35,#f7931e)', borderRadius: 18, padding: '28px 20px', marginBottom: 16, textAlign: 'center', boxShadow: '0 8px 28px rgba(255,107,53,0.35)' },
  csIcon:     { width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' },
  csTitle:    { fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 8 },
  csText:     { fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 },

  card:       { background: '#fff', borderRadius: 16, padding: '16px', marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
  cardHead:   { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f5f5f5' },

  row:        { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  rowIcon:    { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rowTitle:   { fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 3 },
  rowDesc:    { fontSize: 12, color: '#888', lineHeight: 1.5 },

  exampleBox: { background: '#f9fafb', borderRadius: 12, padding: '14px 16px' },
  exRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' },
  exLabel:    { fontSize: 13, color: '#555', fontWeight: 600 },
  exVal:      { fontSize: 16, fontWeight: 800, color: '#1a1a1a' },
  exDivider:  { height: 1, background: '#e5e7eb', margin: '4px 0' },

  statusCard: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 },
};